// src/pages/user/UserDashboardHome.jsx - FIXED ENERGY INCOME WITH LEVEL INCOME COUNT AND TEAM REWARDS
// MOBILE-FIRST REDESIGNED LAYOUT
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { database, ref, get, onValue } from '../../firebase';
import { 
  Package, TrendingUp, Wallet, Users, DollarSign, Gift, Award, 
  Trophy, Zap, Crown, Star, Calendar, Clock,
  ChevronRight, Activity, Sparkles, CreditCard,
  Battery, RefreshCw, Target, Home, ArrowUp, BarChart,
  Shield, AlertCircle, Lock, Layers, Grid, PieChart,
  CircleDot, Gauge, Medal, Flame, Gem, Coins
} from 'lucide-react';

// Dialog Component
const Dialog = ({ isOpen, onClose, title, message, type = 'info' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl p-6 max-w-sm w-full shadow-2xl">
        <div className="text-center">
          <div className="mb-4">
            {type === 'success' ? (
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <div className="text-green-400 text-2xl">✓</div>
              </div>
            ) : type === 'error' ? (
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <div className="text-red-400 text-2xl">✗</div>
              </div>
            ) : (
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <div className="text-blue-400 text-2xl">!</div>
              </div>
            )}
          </div>
          
          <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
          <p className="text-muted-foreground mb-6">{message}</p>
          
          <button
            onClick={onClose}
            className="btn-electric px-6 py-2 rounded-lg w-full"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const UserDashboardHome = ({ downlineActivationStatus, userDataFromAutoLogin }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    // User Info
    userName: 'Loading...',
    userId: '',
    sponsorId: 'N/A',
    email: 'Not provided',
    mobile: 'Not provided',
    joiningDate: 'N/A',
    lastActive: 'N/A',
    status: 'Inactive',
    
    // Wallet Balances
    aurumWallet: 0,
    fundWallet: 0,
    workingWallet: 0,
    bonusWallet: 0,
    energyWallet: 0,
    roiWallet: 0,
    totalWallets: 0,
    
    // Packages
    packageAmount: 0,
    packageCount: 0,
    activePackageCount: 0,
    pausedPackageCount: 0,
    blockedPackageCount: 0,
    
    // Team Statistics
    totalDirects: 0,
    activeDirects: 0,
    inactiveDirects: 0,
    
    // Income Statistics
    roiIncomeTotal: 0,
    roiIncomeMonthly: 0,
    levelIncome: 0,
    levelIncomeCount: 0,
    levelIncomeStatus: 'active',
    levelIncomeBlockedAmount: 0,
    levelIncomeBlockedCount: 0,
    energyIncome: 0,
    energyIncomeStatus: 'active',
    totalIncome: 0,
    
    // Blocked Statistics
    blockedROIIncome: 0,
    blockedLevelIncome: 0,
    blockedEnergyIncome: 0,
    blockedRewards: 0,
    blockedLevelIncomeCount: 0,
    
    // Reward Statistics
    salesRewards: 0,
    salesRewardsCount: 0,
    teamSalesRewards: 0,
    teamSalesRewardsCount: 0,
    totalRewards: 0,
    totalRewardsCount: 0,
    
    // ROI Projection
    dailyROI: 0,
    weeklyROI: 0,
    monthlyROI: 0,
    blockedDailyROI: 0,
    
    // Energy Statistics
    energyFromDirects: 0,
    maxEnergyPercent: 0,
    
    // ROI Wallet
    roiWalletBalance: 0,
    dailyRoiProjection: 0,
    
    // ROI Status
    roiStatus: 'active',
    roiBlockedAmount: 0,
    hasBlockedROI: false,
    
    // Status Indicators
    hasBlockedItems: false,
    hasBlockedLevelIncome: false,
    blockedMessage: '',
    
    // Downline Activation Status
    downlineActivationStatus: downlineActivationStatus || 'enabled'
  });
  
  const [loading, setLoading] = useState(true);
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [activeTab, setActiveTab] = useState('overview');

  // Update downlineActivationStatus when prop changes
  useEffect(() => {
    if (downlineActivationStatus) {
      setDashboardData(prev => ({
        ...prev,
        downlineActivationStatus: downlineActivationStatus
      }));
    }
  }, [downlineActivationStatus]);

  useEffect(() => {
    console.log('UserDashboardHome mounted - Checking authentication...');
    
    if (userDataFromAutoLogin) {
      console.log('Received userDataFromAutoLogin prop:', userDataFromAutoLogin);
      setIsAutoLogin(true);
      initializeWithAutoLoginData(userDataFromAutoLogin);
      return;
    }
    
    const autoLoginToken = localStorage.getItem('autoLoginToken');
    const autoLoginUserId = localStorage.getItem('autoLoginUserId');
    const autoLoginUserName = localStorage.getItem('autoLoginUserName');
    const autoLoginUserData = localStorage.getItem('autoLoginUserData');
    
    const urlParams = new URLSearchParams(window.location.search);
    const isAutoLoginParam = urlParams.get('autoLogin') === 'true';
    const tokenFromUrl = urlParams.get('token');
    
    if (isAutoLoginParam && tokenFromUrl && autoLoginToken === tokenFromUrl && autoLoginUserId && autoLoginUserName) {
      console.log('Auto-login session detected from localStorage');
      setIsAutoLogin(true);
      
      if (autoLoginUserName && autoLoginUserName !== 'Loading...') {
        setDashboardData(prev => ({
          ...prev,
          userName: autoLoginUserName,
          userId: autoLoginUserId || ''
        }));
      }
      
      if (autoLoginUserData) {
        try {
          const userData = JSON.parse(autoLoginUserData);
          console.log('Parsed auto-login data from localStorage:', userData);
          initializeWithAutoLoginData(userData);
        } catch (error) {
          console.error('Error parsing auto-login data:', error);
          findAndLoadUserData(autoLoginUserId);
        }
      } else {
        findAndLoadUserData(autoLoginUserId);
      }
      
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      console.log('Normal login flow');
      checkNormalLogin();
    }
    
    const refreshInterval = setInterval(() => {
      const storedUserId = localStorage.getItem('userId');
      const storedDbKey = localStorage.getItem('dbKey');
      const storedRegistrationDate = localStorage.getItem('registrationDate');
      
      if (storedUserId && !isAutoLogin) {
        fetchDashboardData(storedDbKey, storedUserId, storedRegistrationDate, true);
      }
    }, 30000);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [navigate, userDataFromAutoLogin]);

  const checkNormalLogin = () => {
    const storedUserId = localStorage.getItem('userId');
    const storedDbKey = localStorage.getItem('dbKey');
    const storedRegistrationDate = localStorage.getItem('registrationDate');
    const storedUserName = localStorage.getItem('userName');
    
    if (!storedUserId) {
      navigate('/login');
      return;
    }
    
    if (storedUserName && storedUserName !== 'Loading...') {
      setDashboardData(prev => ({
        ...prev,
        userName: storedUserName,
        userId: storedUserId
      }));
    }
    
    fetchDashboardData(storedDbKey, storedUserId, storedRegistrationDate);
    
    let userUnsubscribe = () => {};
    
    if (storedDbKey && storedRegistrationDate) {
      try {
        const userRef = ref(database, `users/${storedRegistrationDate}/${storedDbKey}`);
        userUnsubscribe = onValue(userRef, (snapshot) => {
          if (snapshot.exists()) {
            const userData = snapshot.val();
            updateWalletBalances(userData);
            updatePackageInfo(userData);
            updateUserInfo(userData);
            updateLevelIncomeStatus(userData);
            updateROIStatus(userData);
            updateEnergyIncome(userData);
            updateRewardsData(userData);
          }
        });
      } catch (error) {
        console.error('Error setting up user listener:', error);
      }
    }
    
    return () => {
      userUnsubscribe();
    };
  };

  // FIXED: Properly update energy income from energyWalletIncomes
  const updateEnergyIncome = (userData) => {
    console.log('Updating energy income from userData:', userData);
    
    const energyWalletBalance = parseFloat(userData.wallets?.energy) || 0;
    
    // Get energy income from energyWalletIncomes
    let totalEnergyIncome = 0;
    let blockedEnergyIncome = 0;
    
    if (userData.energyWalletIncomes) {
      console.log('Found energyWalletIncomes:', userData.energyWalletIncomes);
      
      // Handle if energyWalletIncomes is an object with keys
      Object.values(userData.energyWalletIncomes).forEach(income => {
        if (income && typeof income === 'object') {
          const amount = parseFloat(income.amount) || 0;
          const status = income.status || 'active';
          
          if (status === 'blocked') {
            blockedEnergyIncome += amount;
          } else {
            totalEnergyIncome += amount;
          }
        }
      });
      
      console.log(`Calculated energy income: total=${totalEnergyIncome}, blocked=${blockedEnergyIncome}, balance=${energyWalletBalance}`);
    } else {
      console.log('No energyWalletIncomes found in userData');
    }
    
    // Also check for energyIncomes array (alternative structure)
    if (userData.energyIncomes && Array.isArray(userData.energyIncomes)) {
      userData.energyIncomes.forEach(income => {
        const amount = parseFloat(income.amount) || 0;
        const status = income.status || 'active';
        
        if (status === 'blocked') {
          blockedEnergyIncome += amount;
        } else {
          totalEnergyIncome += amount;
        }
      });
    }
    
    // If no energy income found from incomes, check if energy wallet balance should be counted
    // Some systems might store the total in the wallet balance directly
    if (totalEnergyIncome === 0 && energyWalletBalance > 0) {
      // Check if this is from direct commissions
      totalEnergyIncome = energyWalletBalance;
    }
    
    setDashboardData(prev => ({
      ...prev,
      energyIncome: totalEnergyIncome,
      energyWallet: energyWalletBalance,
      blockedEnergyIncome: blockedEnergyIncome,
      totalIncome: prev.totalIncome - prev.energyIncome + totalEnergyIncome // Adjust total income
    }));
  };

  // FIXED: Function to fetch energy income from multiple sources
  const fetchEnergyIncomeData = async (userId, dbKey) => {
    try {
      let totalEnergyIncome = 0;
      let blockedEnergyIncome = 0;
      let energyWalletBalance = 0;
      
      console.log('Fetching energy income data for:', { userId, dbKey });
      
      // Method 1: Check user's own data first
      const usersSnapshot = await get(ref(database, 'users')).catch(() => ({ exists: () => false }));
      
      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        
        // Find the user in the date-based structure
        for (const dateKey in usersData) {
          if (typeof usersData[dateKey] === 'object') {
            for (const userKey in usersData[dateKey]) {
              const user = usersData[dateKey][userKey];
              
              if (user && (user.sponsorId === userId || user.userId === userId || userKey === dbKey)) {
                console.log('Found user data:', user);
                
                // Get energy wallet balance
                energyWalletBalance = parseFloat(user.wallets?.energy) || 0;
                
                // Check energyWalletIncomes
                if (user.energyWalletIncomes) {
                  Object.values(user.energyWalletIncomes).forEach(income => {
                    if (income && typeof income === 'object') {
                      const amount = parseFloat(income.amount) || 0;
                      const status = income.status || 'active';
                      
                      if (status === 'blocked') {
                        blockedEnergyIncome += amount;
                      } else {
                        totalEnergyIncome += amount;
                      }
                    }
                  });
                }
                
                // Check energyIncomes array
                if (user.energyIncomes && Array.isArray(user.energyIncomes)) {
                  user.energyIncomes.forEach(income => {
                    const amount = parseFloat(income.amount) || 0;
                    const status = income.status || 'active';
                    
                    if (status === 'blocked') {
                      blockedEnergyIncome += amount;
                    } else {
                      totalEnergyIncome += amount;
                    }
                  });
                }
                
                // If no incomes found but wallet has balance, use wallet balance
                if (totalEnergyIncome === 0 && energyWalletBalance > 0) {
                  totalEnergyIncome = energyWalletBalance;
                }
                
                break;
              }
            }
          }
        }
      }
      
      // Method 2: Check energyIncomes node in Firebase
      const energyIncomesSnapshot = await get(ref(database, 'energyIncomes')).catch(() => ({ exists: () => false }));
      
      if (energyIncomesSnapshot.exists()) {
        const energyData = energyIncomesSnapshot.val();
        
        // Iterate through all energy incomes
        for (const dateKey in energyData) {
          if (typeof energyData[dateKey] === 'object') {
            for (const userKey in energyData[dateKey]) {
              const userIncomes = energyData[dateKey][userKey];
              
              if (typeof userIncomes === 'object') {
                for (const incomeKey in userIncomes) {
                  const income = userIncomes[incomeKey];
                  
                  // Check if this income belongs to the current user
                  const isUserIncome = 
                    (income.userId === userId) || 
                    (income.sponsorId === userId) || 
                    (income.userId === dbKey) ||
                    (income.sponsorId === dbKey) ||
                    (userKey === dbKey);
                  
                  if (isUserIncome) {
                    const amount = parseFloat(income.amount) || 0;
                    const status = income.status || 'active';
                    
                    if (status === 'blocked') {
                      blockedEnergyIncome += amount;
                    } else {
                      totalEnergyIncome += amount;
                    }
                  }
                }
              }
            }
          }
        }
      }
      
      console.log('Fetched energy income data:', {
        totalEnergyIncome,
        blockedEnergyIncome,
        energyWalletBalance
      });
      
      return {
        totalEnergyIncome,
        blockedEnergyIncome,
        energyWalletBalance
      };
      
    } catch (error) {
      console.error('Error fetching energy income data:', error);
      return {
        totalEnergyIncome: 0,
        blockedEnergyIncome: 0,
        energyWalletBalance: 0
      };
    }
  };

  const updateRewardsData = (userData) => {
    let totalSalesRewards = 0;
    let salesRewardsCount = 0;
    let totalTeamSalesRewards = 0;
    let teamSalesRewardsCount = 0;
    
    // Check for leaderRewards (Sales Rewards)
    if (userData.leaderRewards) {
      Object.values(userData.leaderRewards).forEach(reward => {
        if (reward && reward.status === 'claimed') {
          totalSalesRewards += parseFloat(reward.cashAmount) || 0;
          salesRewardsCount++;
        }
      });
    }
    
    // Check for teamRewards (Team Sales Rewards)
    if (userData.teamRewards) {
      Object.values(userData.teamRewards).forEach(reward => {
        if (reward && reward.status === 'claimed') {
          totalTeamSalesRewards += parseFloat(reward.cashAmount) || 0;
          teamSalesRewardsCount++;
        }
      });
    }
    
    // Check for claimedRewards array (legacy)
    if (userData.claimedRewards && Array.isArray(userData.claimedRewards)) {
      userData.claimedRewards.forEach(reward => {
        if (reward && reward.status === 'claimed') {
          if (reward.type === 'sales' || reward.rewardName?.includes('Sales')) {
            totalSalesRewards += parseFloat(reward.cashAmount) || 0;
            salesRewardsCount++;
          } else if (reward.type === 'team' || reward.rewardName?.includes('Team')) {
            totalTeamSalesRewards += parseFloat(reward.cashAmount) || 0;
            teamSalesRewardsCount++;
          }
        }
      });
    }
    
    const totalRewards = totalSalesRewards + totalTeamSalesRewards;
    const totalRewardsCount = salesRewardsCount + teamSalesRewardsCount;
    
    setDashboardData(prev => ({
      ...prev,
      salesRewards: totalSalesRewards,
      salesRewardsCount: salesRewardsCount,
      teamSalesRewards: totalTeamSalesRewards,
      teamSalesRewardsCount: teamSalesRewardsCount,
      totalRewards: totalRewards,
      totalRewardsCount: totalRewardsCount,
      totalIncome: prev.roiIncomeTotal + prev.levelIncome + prev.energyIncome + totalRewards
    }));
    
    console.log('Rewards updated:', {
      salesRewards: totalSalesRewards,
      salesCount: salesRewardsCount,
      teamRewards: totalTeamSalesRewards,
      teamCount: teamSalesRewardsCount,
      totalRewards
    });
  };

  const updateROIStatus = (userData) => {
    const packages = userData.packages || [];
    
    let isROIBlocked = false;
    let blockedROIAmount = 0;
    let activeROIStatus = 'active';
    
    packages.forEach(pkg => {
      if (pkg.status === 'active' && pkg.roiStatus === 'blocked') {
        isROIBlocked = true;
        activeROIStatus = 'blocked';
        
        const dailyROI = parseFloat(pkg.dailyROI) || 0;
        const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0;
        blockedROIAmount += ((amount * dailyROI) / 100) * 30;
      }
    });
    
    setDashboardData(prev => ({
      ...prev,
      roiStatus: activeROIStatus,
      hasBlockedROI: isROIBlocked,
      blockedROIIncome: blockedROIAmount || prev.blockedROIIncome
    }));
  };

  const initializeWithAutoLoginData = (userData) => {
    console.log('Initializing with auto-login data:', userData);
    
    const {
      userId,
      sponsorId,
      name,
      fullName,
      email,
      mobile,
      registrationDate,
      lastLogin,
      packages = [],
      wallets = {},
      fund = {},
      referralCount = 0,
      status = 'inactive',
      levelIncomeStatus = 'active',
      downlineActivationStatus: downlineStatus,
      leaderRewards,
      teamRewards,
      energyWalletIncomes // Make sure to include this
    } = userData;
    
    const displayName = name || fullName || 'User';
    const sponsorIdValue = sponsorId || userId || '';
    
    const joinDate = registrationDate
      ? new Date(registrationDate).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      : 'N/A';

    const lastDate = lastLogin
      ? new Date(lastLogin).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'N/A';

    // Wallet balances
    const aurumBalance = parseFloat(wallets?.aurum) || 0;
    const fundBalance = parseFloat(fund?.balance) || 0;
    const workingBalance = parseFloat(wallets?.working) || 0;
    const energyBalance = parseFloat(wallets?.energy) || 0;
    const roiBalance = parseFloat(wallets?.roi) || 0;
    
    // Package info
    const activePackages = Array.isArray(packages) ? packages.filter(pkg => pkg.status === 'active') : [];
    const pausedPackages = Array.isArray(packages) ? packages.filter(pkg => pkg.status === 'paused') : [];
    const blockedPackages = Array.isArray(packages) ? packages.filter(pkg => pkg.status === 'blocked') : [];
    const packageTotal = activePackages.reduce((sum, pkg) => sum + (parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0), 0);
    
    const dailyRoiProjection = activePackages.reduce((sum, pkg) => {
      const dailyROI = parseFloat(pkg.dailyROI) || 0;
      const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0;
      return sum + ((amount * dailyROI) / 100);
    }, 0);
    
    const blockedDailyROI = blockedPackages.reduce((sum, pkg) => {
      const dailyROI = parseFloat(pkg.dailyROI) || 0;
      const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0;
      return sum + ((amount * dailyROI) / 100);
    }, 0);
    
    // Check ROI status
    let isROIBlocked = false;
    let roiBlockedAmount = 0;
    activePackages.forEach(pkg => {
      if (pkg.roiStatus === 'blocked') {
        isROIBlocked = true;
        const dailyROI = parseFloat(pkg.dailyROI) || 0;
        const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0;
        roiBlockedAmount += ((amount * dailyROI) / 100) * 30;
      }
    });
    
    const totalDirects = parseInt(referralCount) || 0;
    
    let userStatus = 'Inactive';
    if (activePackages.length > 0) {
      userStatus = 'Active';
    } else if (blockedPackages.length > 0) {
      userStatus = 'Blocked';
    } else if (pausedPackages.length > 0) {
      userStatus = 'Paused';
    } else {
      userStatus = status || 'Inactive';
    }
    
    const hasBlockedLevelIncome = levelIncomeStatus === 'blocked';
    
    // FIXED: REAL ENERGY INCOME from energyWalletIncomes
    let realEnergyIncome = 0;
    let blockedEnergyIncome = 0;
    
    if (energyWalletIncomes) {
      console.log('Found energyWalletIncomes in auto-login data:', energyWalletIncomes);
      
      Object.values(energyWalletIncomes).forEach(income => {
        if (income && typeof income === 'object') {
          const amount = parseFloat(income.amount) || 0;
          const status = income.status || 'active';
          
          if (status === 'blocked') {
            blockedEnergyIncome += amount;
          } else {
            realEnergyIncome += amount;
          }
        }
      });
    } else {
      console.log('No energyWalletIncomes found in auto-login data');
      // If no incomes but wallet has balance, use that
      if (energyBalance > 0) {
        realEnergyIncome = energyBalance;
      }
    }
    
    console.log(`Auto-login energy income: total=${realEnergyIncome}, blocked=${blockedEnergyIncome}, balance=${energyBalance}`);
    
    // ACTUAL ROI INCOME FROM WALLET
    const actualRoiIncome = roiBalance;
    
    // Calculate rewards from auto-login data
    let totalSalesRewards = 0;
    let salesRewardsCount = 0;
    let totalTeamSalesRewards = 0;
    let teamSalesRewardsCount = 0;
    
    // Check leaderRewards
    if (leaderRewards) {
      Object.values(leaderRewards).forEach(reward => {
        if (reward && reward.status === 'claimed') {
          totalSalesRewards += parseFloat(reward.cashAmount) || 0;
          salesRewardsCount++;
        }
      });
    }
    
    // Check teamRewards
    if (teamRewards) {
      Object.values(teamRewards).forEach(reward => {
        if (reward && reward.status === 'claimed') {
          totalTeamSalesRewards += parseFloat(reward.cashAmount) || 0;
          teamSalesRewardsCount++;
        }
      });
    }
    
    const totalRewards = totalSalesRewards + totalTeamSalesRewards;
    
    const updatedData = {
      // User Info
      userName: displayName,
      userId: sponsorIdValue,
      sponsorId: sponsorIdValue,
      email: email || 'Not provided',
      mobile: mobile || 'Not provided',
      joiningDate: joinDate,
      lastActive: lastDate,
      status: userStatus,
      
      // Wallet Balances
      aurumWallet: aurumBalance,
      fundWallet: fundBalance,
      workingWallet: workingBalance,
      energyWallet: energyBalance,
      roiWallet: roiBalance,
      totalWallets: aurumBalance + fundBalance + workingBalance + energyBalance + roiBalance,
      
      // Package Info
      packageAmount: packageTotal,
      packageCount: packages.length,
      activePackageCount: activePackages.length,
      pausedPackageCount: pausedPackages.length,
      blockedPackageCount: blockedPackages.length,
      
      // Team Statistics
      totalDirects: totalDirects,
      activeDirects: Math.floor(totalDirects * 0.7),
      inactiveDirects: Math.floor(totalDirects * 0.3),
      
      // ROI Wallet
      roiWalletBalance: roiBalance,
      
      // Daily ROI Projection
      dailyRoiProjection: dailyRoiProjection,
      dailyROI: dailyRoiProjection,
      weeklyROI: dailyRoiProjection * 7,
      monthlyROI: dailyRoiProjection * 30,
      blockedDailyROI: blockedDailyROI,
      
      // ROI Status
      roiStatus: isROIBlocked ? 'blocked' : 'active',
      hasBlockedROI: isROIBlocked,
      blockedROIIncome: roiBlockedAmount,
      
      // Level income - will be updated by fetchDashboardData
      levelIncome: 0,
      levelIncomeCount: 0,
      levelIncomeStatus: levelIncomeStatus,
      levelIncomeBlockedAmount: 0,
      levelIncomeBlockedCount: 0,
      hasBlockedLevelIncome: hasBlockedLevelIncome,
      blockedLevelIncomeCount: 0,
      
      // Income - FIXED: Use real energy income
      roiIncomeTotal: actualRoiIncome,
      roiIncomeMonthly: actualRoiIncome * 0.3,
      energyIncome: realEnergyIncome,
      blockedEnergyIncome: blockedEnergyIncome,
      totalIncome: actualRoiIncome + realEnergyIncome + totalRewards,
      
      // Rewards
      salesRewards: totalSalesRewards,
      salesRewardsCount: salesRewardsCount,
      teamSalesRewards: totalTeamSalesRewards,
      teamSalesRewardsCount: teamSalesRewardsCount,
      totalRewards: totalRewards,
      totalRewardsCount: salesRewardsCount + teamSalesRewardsCount,
      
      // Blocked statistics
      blockedROIIncome: roiBlockedAmount,
      blockedLevelIncome: 0,
      blockedEnergyIncome: blockedEnergyIncome,
      blockedRewards: 0,
      
      // Downline activation status
      downlineActivationStatus: downlineStatus || 'enabled',
      
      // Status indicators
      hasBlockedItems: blockedPackages.length > 0 || blockedDailyROI > 0 || hasBlockedLevelIncome || isROIBlocked || blockedEnergyIncome > 0,
      blockedMessage: [
        blockedPackages.length > 0 ? `${blockedPackages.length} blocked package(s)` : '',
        hasBlockedLevelIncome ? 'Level income blocked' : '',
        isROIBlocked ? 'ROI blocked' : '',
        blockedEnergyIncome > 0 ? `$${blockedEnergyIncome.toFixed(2)} energy blocked` : ''
      ].filter(Boolean).join(', ')
    };
    
    console.log('Setting immediate data from auto-login:', updatedData);
    
    setDashboardData(prev => ({
      ...prev,
      ...updatedData
    }));
    
    setLoading(false);
    
    setTimeout(() => {
      findAndLoadUserData(sponsorIdValue);
    }, 100);
  };

  const findAndLoadUserData = async (userIdToFind) => {
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const usersByDate = snapshot.val();
        let foundUserData = null;
        let foundDbKey = null;
        let foundDate = null;
        
        for (const date in usersByDate) {
          if (typeof usersByDate[date] === 'object') {
            for (const [dbKey, user] of Object.entries(usersByDate[date])) {
              if (user && (user.userId === userIdToFind || user.sponsorId === userIdToFind)) {
                foundUserData = user;
                foundDbKey = dbKey;
                foundDate = date;
                break;
              }
            }
          }
          if (foundUserData) break;
        }
        
        if (foundUserData && foundDbKey && foundDate) {
          localStorage.setItem('userId', foundUserData.userId || foundUserData.sponsorId);
          localStorage.setItem('userName', foundUserData.name);
          localStorage.setItem('userSponsorId', foundUserData.sponsorId);
          localStorage.setItem('dbKey', foundDbKey);
          localStorage.setItem('registrationDate', foundDate);
          
          fetchDashboardData(foundDbKey, userIdToFind, foundDate, true);
        }
      }
    } catch (error) {
      console.error('Error finding user data:', error);
    }
  };

  const updateWalletBalances = (userData) => {
    const aurumBalance = parseFloat(userData.wallets?.aurum) || 0;
    const fundBalance = parseFloat(userData.fund?.balance) || 0;
    const workingBalance = parseFloat(userData.wallets?.working) || 0;
    const energyBalance = parseFloat(userData.wallets?.energy) || 0;
    const roiBalance = parseFloat(userData.wallets?.roi) || 0;
    
    setDashboardData(prev => ({
      ...prev,
      aurumWallet: aurumBalance,
      fundWallet: fundBalance,
      workingWallet: workingBalance,
      energyWallet: energyBalance,
      roiWallet: roiBalance,
      roiWalletBalance: roiBalance,
      totalWallets: aurumBalance + fundBalance + workingBalance + energyBalance + roiBalance,
      
      roiIncomeTotal: roiBalance,
      totalIncome: roiBalance + prev.levelIncome + prev.energyIncome + prev.totalRewards
    }));
  };

  const updatePackageInfo = (userData) => {
    const packages = userData.packages || [];
    const activePackages = packages.filter(pkg => pkg.status === 'active');
    const pausedPackages = packages.filter(pkg => pkg.status === 'paused');
    const blockedPackages = packages.filter(pkg => pkg.status === 'blocked');
    const packageTotal = activePackages.reduce((sum, pkg) => sum + (parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0), 0);
    
    let isROIBlocked = false;
    activePackages.forEach(pkg => {
      if (pkg.roiStatus === 'blocked') {
        isROIBlocked = true;
      }
    });
    
    const dailyRoiProjection = activePackages.reduce((sum, pkg) => {
      const dailyROI = parseFloat(pkg.dailyROI) || 0;
      const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0;
      return sum + ((amount * dailyROI) / 100);
    }, 0);
    
    const blockedDailyROI = blockedPackages.reduce((sum, pkg) => {
      const dailyROI = parseFloat(pkg.dailyROI) || 0;
      const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.investmentAmount) || 0;
      return sum + ((amount * dailyROI) / 100);
    }, 0);
    
    setDashboardData(prev => ({
      ...prev,
      packageAmount: packageTotal,
      packageCount: packages.length,
      activePackageCount: activePackages.length,
      pausedPackageCount: pausedPackages.length,
      blockedPackageCount: blockedPackages.length,
      dailyRoiProjection: dailyRoiProjection,
      dailyROI: dailyRoiProjection,
      weeklyROI: dailyRoiProjection * 7,
      monthlyROI: dailyRoiProjection * 30,
      blockedDailyROI: blockedDailyROI,
      roiStatus: isROIBlocked ? 'blocked' : 'active',
      hasBlockedROI: isROIBlocked
    }));
  };

  const updateUserInfo = (userData) => {
    const joinDate = userData.registrationDate
      ? new Date(userData.registrationDate).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      : 'N/A';

    const lastDate = userData.lastLogin
      ? new Date(userData.lastLogin).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'N/A';

    const packages = userData.packages || [];
    const hasActivePackages = packages.some(pkg => pkg.status === 'active');
    const hasBlockedPackages = packages.some(pkg => pkg.status === 'blocked');
    
    let status = 'Inactive';
    if (hasActivePackages) {
      status = 'Active';
    } else if (hasBlockedPackages) {
      status = 'Blocked';
    } else if (packages.some(pkg => pkg.status === 'paused')) {
      status = 'Paused';
    }

    setDashboardData(prev => ({
      ...prev,
      userName: userData.name || prev.userName || 'User',
      userId: userData.sponsorId || prev.userId || '',
      sponsorId: userData.sponsorId || prev.sponsorId || '',
      email: userData.email || prev.email || 'Not provided',
      mobile: userData.mobile || prev.mobile || 'Not provided',
      joiningDate: joinDate || prev.joiningDate,
      lastActive: lastDate || prev.lastActive,
      status: status || prev.status,
      downlineActivationStatus: userData.downlineActivationStatus || prev.downlineActivationStatus || 'enabled'
    }));
  };

  const updateLevelIncomeStatus = (userData) => {
    const levelIncomeStatus = userData.levelIncomeStatus || 'active';
    const isBlocked = levelIncomeStatus === 'blocked';
    
    setDashboardData(prev => {
      const previousLevelIncome = prev.levelIncome;
      
      const updatedData = {
        levelIncomeStatus: levelIncomeStatus,
        hasBlockedLevelIncome: isBlocked,
        levelIncome: isBlocked ? 0 : previousLevelIncome,
        levelIncomeBlockedAmount: isBlocked ? previousLevelIncome : 0
      };
      
      if (isBlocked) {
        updatedData.totalIncome = prev.totalIncome - previousLevelIncome;
      }
      
      const messages = [];
      if (prev.blockedPackageCount > 0) messages.push(`${prev.blockedPackageCount} blocked package(s)`);
      if (isBlocked) messages.push('Level income blocked');
      if (prev.hasBlockedROI) messages.push('ROI blocked');
      if (prev.blockedDailyROI > 0) messages.push(`$${prev.blockedDailyROI.toFixed(2)} ROI blocked`);
      if (prev.blockedEnergyIncome > 0) messages.push(`$${prev.blockedEnergyIncome.toFixed(2)} energy blocked`);
      
      updatedData.hasBlockedItems = prev.blockedPackageCount > 0 || isBlocked || prev.hasBlockedROI || prev.blockedDailyROI > 0 || prev.blockedEnergyIncome > 0;
      updatedData.blockedMessage = messages.join(', ');
      
      return {
        ...prev,
        ...updatedData
      };
    });
  };

  // FIXED: Properly fetch Level Income with count and amount
  const fetchLevelIncomeData = async (sponsorId, dbKey) => {
    try {
      let levelIncomeTotal = 0;
      let levelIncomeCount = 0;
      let blockedLevelIncomeTotal = 0;
      let blockedLevelIncomeCount = 0;
      
      // Fetch from levelIncomes node
      const levelIncomesSnapshot = await get(ref(database, 'levelIncomes')).catch(() => ({ exists: () => false }));
      
      if (levelIncomesSnapshot.exists()) {
        const levelData = levelIncomesSnapshot.val();
        
        // Iterate through all level incomes
        for (const dateKey in levelData) {
          if (typeof levelData[dateKey] === 'object') {
            for (const userKey in levelData[dateKey]) {
              const userIncomes = levelData[dateKey][userKey];
              
              if (typeof userIncomes === 'object') {
                for (const incomeKey in userIncomes) {
                  const income = userIncomes[incomeKey];
                  
                  // Check if this income belongs to the current user
                  const isUserIncome = 
                    (income.userId === sponsorId) || 
                    (income.sponsorId === sponsorId) || 
                    (income.userId === dbKey) ||
                    (income.sponsorId === dbKey) ||
                    (userKey === dbKey);
                  
                  if (isUserIncome) {
                    const amount = parseFloat(income.amount) || 0;
                    const status = income.status || 'active';
                    
                    if (status === 'blocked') {
                      blockedLevelIncomeTotal += amount;
                      blockedLevelIncomeCount++;
                    } else {
                      levelIncomeTotal += amount;
                      levelIncomeCount++;
                    }
                  }
                }
              }
            }
          }
        }
      }
      
      // Also check user's own data for level incomes stored in user node
      const userSnapshot = await get(ref(database, `users`)).catch(() => ({ exists: () => false }));
      if (userSnapshot.exists()) {
        const usersData = userSnapshot.val();
        
        for (const dateKey in usersData) {
          if (typeof usersData[dateKey] === 'object') {
            for (const userKey in usersData[dateKey]) {
              const user = usersData[dateKey][userKey];
              
              if (user && (user.sponsorId === sponsorId || userKey === dbKey)) {
                // Check for levelIncomes array in user data
                if (user.levelIncomes && Array.isArray(user.levelIncomes)) {
                  user.levelIncomes.forEach(income => {
                    const amount = parseFloat(income.amount) || 0;
                    const status = income.status || 'active';
                    
                    if (status === 'blocked') {
                      blockedLevelIncomeTotal += amount;
                      blockedLevelIncomeCount++;
                    } else {
                      levelIncomeTotal += amount;
                      levelIncomeCount++;
                    }
                  });
                }
                
                // Check for levelIncome in wallets or other structures
                if (user.wallets && user.wallets.levelIncome) {
                  const amount = parseFloat(user.wallets.levelIncome) || 0;
                  levelIncomeTotal += amount;
                  levelIncomeCount++;
                }
              }
            }
          }
        }
      }
      
      return {
        levelIncomeTotal,
        levelIncomeCount,
        blockedLevelIncomeTotal,
        blockedLevelIncomeCount
      };
      
    } catch (error) {
      console.error('Error fetching level income data:', error);
      return {
        levelIncomeTotal: 0,
        levelIncomeCount: 0,
        blockedLevelIncomeTotal: 0,
        blockedLevelIncomeCount: 0
      };
    }
  };

  // FIXED: Function to fetch rewards data
  const fetchRewardsData = async (userId, dbKey) => {
    try {
      let totalSalesRewards = 0;
      let salesRewardsCount = 0;
      let totalTeamSalesRewards = 0;
      let teamSalesRewardsCount = 0;
      
      // Fetch user data to get rewards
      const usersSnapshot = await get(ref(database, 'users')).catch(() => ({ exists: () => false }));
      
      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        
        // Find the user in the date-based structure
        for (const dateKey in usersData) {
          if (typeof usersData[dateKey] === 'object') {
            for (const userKey in usersData[dateKey]) {
              const user = usersData[dateKey][userKey];
              
              if (user && (user.sponsorId === userId || userKey === dbKey)) {
                // Check leaderRewards (Sales Rewards)
                if (user.leaderRewards) {
                  Object.values(user.leaderRewards).forEach(reward => {
                    if (reward && reward.status === 'claimed') {
                      totalSalesRewards += parseFloat(reward.cashAmount) || 0;
                      salesRewardsCount++;
                    }
                  });
                }
                
                // Check teamRewards (Team Sales Rewards)
                if (user.teamRewards) {
                  Object.values(user.teamRewards).forEach(reward => {
                    if (reward && reward.status === 'claimed') {
                      totalTeamSalesRewards += parseFloat(reward.cashAmount) || 0;
                      teamSalesRewardsCount++;
                    }
                  });
                }
                
                // Check claimedRewards array (legacy)
                if (user.claimedRewards && Array.isArray(user.claimedRewards)) {
                  user.claimedRewards.forEach(reward => {
                    if (reward && reward.status === 'claimed') {
                      if (reward.type === 'sales' || reward.rewardName?.includes('Sales')) {
                        totalSalesRewards += parseFloat(reward.cashAmount) || 0;
                        salesRewardsCount++;
                      } else if (reward.type === 'team' || reward.rewardName?.includes('Team')) {
                        totalTeamSalesRewards += parseFloat(reward.cashAmount) || 0;
                        teamSalesRewardsCount++;
                      }
                    }
                  });
                }
                
                break;
              }
            }
          }
        }
      }
      
      return {
        salesRewards: totalSalesRewards,
        salesRewardsCount: salesRewardsCount,
        teamSalesRewards: totalTeamSalesRewards,
        teamSalesRewardsCount: teamSalesRewardsCount,
        totalRewards: totalSalesRewards + totalTeamSalesRewards,
        totalRewardsCount: salesRewardsCount + teamSalesRewardsCount
      };
      
    } catch (error) {
      console.error('Error fetching rewards data:', error);
      return {
        salesRewards: 0,
        salesRewardsCount: 0,
        teamSalesRewards: 0,
        teamSalesRewardsCount: 0,
        totalRewards: 0,
        totalRewardsCount: 0
      };
    }
  };

  const fetchDashboardData = async (dbKey, sponsorId, registrationDate, isSilent = false) => {
    try {
      if (!isSilent) {
        setLoading(true);
      }

      console.log('Fetching dashboard data for:', { dbKey, sponsorId, registrationDate });

      let userDataVal = {};
      if (dbKey && registrationDate) {
        const userSnapshot = await get(ref(database, `users/${registrationDate}/${dbKey}`));
        if (userSnapshot.exists()) {
          userDataVal = userSnapshot.val();
          console.log('User data loaded from Firebase:', userDataVal);
          
          updateWalletBalances(userDataVal);
          updatePackageInfo(userDataVal);
          updateUserInfo(userDataVal);
          updateROIStatus(userDataVal);
          updateEnergyIncome(userDataVal);
          updateRewardsData(userDataVal);
        }
      }

      // Fetch Level Income data with count and amount
      const levelIncomeData = await fetchLevelIncomeData(sponsorId, dbKey);
      
      const {
        levelIncomeTotal,
        levelIncomeCount,
        blockedLevelIncomeTotal,
        blockedLevelIncomeCount
      } = levelIncomeData;

      // Fetch Energy Income data separately to ensure we get it
      const energyIncomeData = await fetchEnergyIncomeData(sponsorId, dbKey);
      
      const {
        totalEnergyIncome,
        blockedEnergyIncome,
        energyWalletBalance
      } = energyIncomeData;

      // Fetch Rewards data
      const rewardsData = await fetchRewardsData(sponsorId, dbKey);

      const [
        roiIncomesSnapshot,
        directTeamSnapshot
      ] = await Promise.all([
        get(ref(database, 'roiIncomes')).catch(() => ({ exists: () => false })),
        get(ref(database, 'users'))
      ]);

      // Calculate Level Income Status
      let levelIncomeStatus = userDataVal.levelIncomeStatus || 'active';
      const isLevelIncomeBlocked = levelIncomeStatus === 'blocked';

      // Calculate Direct Team Stats
      let totalDirects = 0;
      let activeDirects = 0;
      let inactiveDirects = 0;
      
      if (directTeamSnapshot.exists()) {
        const allUsers = directTeamSnapshot.val();
        
        for (const dateKey in allUsers) {
          if (typeof allUsers[dateKey] === 'object') {
            for (const userKey in allUsers[dateKey]) {
              const user = allUsers[dateKey][userKey];
              if (user && user.uplineSponsorId === sponsorId) {
                totalDirects++;
                const hasActivePackage = (user.packages || []).some(pkg => pkg.status === 'active');
                if (hasActivePackage) {
                  activeDirects++;
                } else {
                  inactiveDirects++;
                }
              }
            }
          }
        }
      }

      // Calculate ROI Income
      let roiIncomeTotal = 0;
      let roiIncomeMonthly = 0;
      let blockedROIIncomeTotal = 0;
      const now = new Date();
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      if (roiIncomesSnapshot.exists()) {
        const roiIncomeData = roiIncomesSnapshot.val();
        
        for (const dateKey in roiIncomeData) {
          if (typeof roiIncomeData[dateKey] === 'object') {
            for (const userKey in roiIncomeData[dateKey]) {
              const userIncomes = roiIncomeData[dateKey][userKey];
              if (typeof userIncomes === 'object') {
                for (const incomeKey in userIncomes) {
                  const income = userIncomes[incomeKey];
                  if (income && (income.userId === sponsorId || income.sponsorId === sponsorId || userKey === dbKey)) {
                    const amount = parseFloat(income.amount) || 0;
                    const isBlocked = income.status === 'blocked';
                    
                    if (isBlocked) {
                      blockedROIIncomeTotal += amount;
                    } else {
                      roiIncomeTotal += amount;
                      if (income.date && new Date(income.date) > monthAgo) {
                        roiIncomeMonthly += amount;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      // Check if ROI is blocked
      let isROIBlocked = false;
      if (userDataVal.packages) {
        isROIBlocked = userDataVal.packages.some(pkg => 
          pkg.status === 'active' && pkg.roiStatus === 'blocked'
        );
      }

      // Get REAL energy income - prioritize fetched data
      const finalEnergyIncome = totalEnergyIncome > 0 ? totalEnergyIncome : 
                               (parseFloat(userDataVal.wallets?.energy) || 0);

      console.log('Final energy income:', {
        fromFetch: totalEnergyIncome,
        fromWallet: parseFloat(userDataVal.wallets?.energy) || 0,
        using: finalEnergyIncome,
        blocked: blockedEnergyIncome
      });

      // Calculate final values based on blocked status
      const finalLevelIncome = isLevelIncomeBlocked ? 0 : levelIncomeTotal;
      const finalLevelIncomeCount = isLevelIncomeBlocked ? 0 : levelIncomeCount;
      const blockedLevelIncomeAmount = isLevelIncomeBlocked ? levelIncomeTotal : blockedLevelIncomeTotal;
      const finalBlockedLevelIncomeCount = isLevelIncomeBlocked ? levelIncomeCount : blockedLevelIncomeCount;
      
      const actualRoiFromWallet = parseFloat(userDataVal.wallets?.roi) || 0;
      
      // Use rewards data from fetchRewardsData or updateRewardsData
      const finalRewardsData = rewardsData.totalRewards > 0 ? rewardsData : {
        salesRewards: userDataVal.salesRewards || 0,
        salesRewardsCount: userDataVal.salesRewardsCount || 0,
        teamSalesRewards: userDataVal.teamSalesRewards || 0,
        teamSalesRewardsCount: userDataVal.teamSalesRewardsCount || 0,
        totalRewards: (userDataVal.salesRewards || 0) + (userDataVal.teamSalesRewards || 0),
        totalRewardsCount: (userDataVal.salesRewardsCount || 0) + (userDataVal.teamSalesRewardsCount || 0)
      };
      
      setDashboardData(prev => ({
        ...prev,
        // Team Statistics
        totalDirects: totalDirects || prev.totalDirects,
        activeDirects: activeDirects || prev.activeDirects,
        inactiveDirects: inactiveDirects || prev.inactiveDirects,
        
        // Income Statistics
        roiIncomeTotal: actualRoiFromWallet || prev.roiWalletBalance || prev.roiIncomeTotal,
        roiIncomeMonthly: roiIncomeMonthly || prev.roiIncomeMonthly,
        
        // Level Income
        levelIncome: finalLevelIncome,
        levelIncomeCount: finalLevelIncomeCount,
        levelIncomeStatus: levelIncomeStatus,
        levelIncomeBlockedAmount: blockedLevelIncomeAmount,
        levelIncomeBlockedCount: finalBlockedLevelIncomeCount,
        hasBlockedLevelIncome: isLevelIncomeBlocked,
        blockedLevelIncomeCount: finalBlockedLevelIncomeCount,
        blockedLevelIncome: blockedLevelIncomeAmount,
        
        // Energy Income - FIXED: Use the fetched energy income
        energyIncome: finalEnergyIncome,
        energyWallet: energyWalletBalance || parseFloat(userDataVal.wallets?.energy) || 0,
        blockedEnergyIncome: blockedEnergyIncome,
        
        // ROI Status
        roiStatus: isROIBlocked ? 'blocked' : 'active',
        hasBlockedROI: isROIBlocked,
        
        // Blocked statistics
        blockedROIIncome: blockedROIIncomeTotal || prev.blockedROIIncome,
        
        // Rewards
        salesRewards: finalRewardsData.salesRewards,
        salesRewardsCount: finalRewardsData.salesRewardsCount,
        teamSalesRewards: finalRewardsData.teamSalesRewards,
        teamSalesRewardsCount: finalRewardsData.teamSalesRewardsCount,
        totalRewards: finalRewardsData.totalRewards,
        totalRewardsCount: finalRewardsData.totalRewardsCount,
        
        // Total Income
        totalIncome: actualRoiFromWallet + finalLevelIncome + finalEnergyIncome + finalRewardsData.totalRewards,
        
        // Status indicators
        hasBlockedItems: blockedROIIncomeTotal > 0 || isLevelIncomeBlocked || isROIBlocked || prev.blockedPackageCount > 0 || blockedEnergyIncome > 0,
        blockedMessage: [
          prev.blockedPackageCount > 0 ? `${prev.blockedPackageCount} blocked package(s)` : '',
          isLevelIncomeBlocked ? `Level income blocked (${finalBlockedLevelIncomeCount} transactions)` : '',
          isROIBlocked ? 'ROI blocked' : '',
          blockedROIIncomeTotal > 0 ? `$${blockedROIIncomeTotal.toFixed(2)} ROI blocked` : '',
          blockedEnergyIncome > 0 ? `$${blockedEnergyIncome.toFixed(2)} energy blocked` : ''
        ].filter(Boolean).join(', ')
      }));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDialog({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load dashboard data',
        type: 'error'
      });
    } finally {
      if (!isSilent) {
        setTimeout(() => {
          setLoading(false);
        }, 300);
      } else {
        setLoading(false);
      }
    }
  };

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    if (num >= 1000) {
      return `$${num.toFixed(0)}`;
    }
    return `$${num.toFixed(2)}`;
  };

  const getStatusBadge = (status, count = 0) => {
    if (status === 'blocked' && count > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-500/20 text-red-400 border border-red-500/30 rounded-full">
          <Shield size={10} />
          {count} Blocked
        </span>
      );
    }
    return null;
  };

  const handleCardClick = (type) => {
    const routes = {
      packages: '/user-dashboard/activate',
      workingWallet: '/user-dashboard/workingwallet',
      aurumWallet: '/user-dashboard/incomes/Aerovoltwallet',
      fundWallet: '/user-dashboard/fundhistory',
      energyWallet: '/user-dashboard/energywallet',
      team: '/user-dashboard/network',
      roiIncome: '/user-dashboard/incomes/roi',
      levelIncome: '/user-dashboard/incomes/level',
      energyIncome: '/user-dashboard/energywallet',
      dailyROI: '/user-dashboard/incomes/roi',
      weeklyROI: '/user-dashboard/incomes/roi',
      monthlyROI: '/user-dashboard/incomes/roi',
      roiWallet: '/user-dashboard/incomes/roiwallet',
      salesRewards: '/user-dashboard/leaderreward',
      teamRewards: '/user-dashboard/teamsalesreward',
      totalRewards: '/user-dashboard/workingwallet',
      leaderReward: '/user-dashboard/leaderreward',
      teamSalesReward: '/user-dashboard/teamsalesreward',
      blockedItems: '/user-dashboard/incomes/blocked',
    };
    
    if (routes[type]) {
      navigate(routes[type]);
    }
  };

  const DownlineActivationWarning = () => {
    if (dashboardData.downlineActivationStatus !== 'disabled') return null;

    return (
      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
        <div className="flex items-center gap-2 text-red-400">
          <Lock size={16} />
          <div className="text-xs sm:text-sm">
            <strong>⚠️ Downline Activation Disabled:</strong> Your downline activation feature has been disabled by the admin.
          </div>
        </div>
      </div>
    );
  };

  // Loading Skeleton - Mobile Optimized
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-16 sm:pt-20 pb-8">
        {/* Header Skeleton */}
        <div className="mb-6 animate-pulse">
          <div className="h-7 bg-secondary rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-secondary rounded w-1/2"></div>
        </div>

        {/* Quick Stats Skeleton - Grid for mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card rounded-xl p-3 animate-pulse">
              <div className="h-4 bg-secondary rounded w-1/2 mb-3"></div>
              <div className="h-6 bg-secondary rounded mb-2"></div>
              <div className="h-3 bg-secondary rounded w-2/3"></div>
            </div>
          ))}
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-8 bg-secondary rounded-full w-20 flex-shrink-0"></div>
          ))}
        </div>

        {/* Content Skeleton */}
        <div className="space-y-4">
          <div className="bg-card rounded-xl p-4 animate-pulse">
            <div className="h-5 bg-secondary rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-2 gap-3">
              {[...Array(6)].map((_, j) => (
                <div key={j} className="h-20 bg-secondary rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog
        isOpen={dialog.isOpen}
        onClose={() => setDialog({ ...dialog, isOpen: false })}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-16 sm:pt-20 pb-8">
        {/* Downline Activation Warning */}
        <DownlineActivationWarning />

        {/* Blocked Items Warning */}
        {dashboardData.hasBlockedItems && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-start gap-2 text-red-400">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <div className="text-xs sm:text-sm">
                <strong className="block sm:inline">⚠️ Blocked Items Detected:</strong> {dashboardData.blockedMessage}
              </div>
            </div>
          </div>
        )}

        {/* Header Section - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-1">
              Welcome back, <span className="text-primary">{dashboardData.userName}</span>!
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground">Track your earnings and manage your investments</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="px-2 sm:px-3 py-1.5 bg-secondary rounded-lg text-xs sm:text-sm flex-1 sm:flex-initial text-center sm:text-left">
              ID: {dashboardData.userId}
            </div>
            <button
              onClick={() => {
                const storedUserId = localStorage.getItem('userId');
                const storedDbKey = localStorage.getItem('dbKey');
                const storedRegistrationDate = localStorage.getItem('registrationDate');
                setLoading(true);
                fetchDashboardData(storedDbKey, storedUserId, storedRegistrationDate);
              }}
              className="p-2 hover:bg-secondary rounded-lg transition flex-shrink-0"
              disabled={loading}
            >
              <RefreshCw size={16} className={`text-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Quick Stats Cards - 2 Column Grid for Mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {/* Total Income Card */}
          <div className="bg-gradient-to-br from-primary/10 to-transparent p-3 rounded-xl border border-primary/20">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-primary" size={18} />
              <span className="text-[10px] text-primary bg-primary/10 px-2 py-0.5 rounded-full">Total</span>
            </div>
            <div className="text-lg sm:text-xl font-bold text-foreground">{formatCurrency(dashboardData.totalIncome)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">All sources</div>
          </div>

          {/* Active Packages Card */}
          <div className="bg-gradient-to-br from-blue-500/10 to-transparent p-3 rounded-xl border border-blue-500/20">
            <div className="flex items-center justify-between mb-2">
              <Package className="text-blue-400" size={18} />
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-full">Active</span>
                {getStatusBadge('blocked', dashboardData.blockedPackageCount)}
              </div>
            </div>
            <div className="text-lg sm:text-xl font-bold text-foreground">{dashboardData.activePackageCount}</div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {formatCurrency(dashboardData.packageAmount)}
            </div>
          </div>

          {/* Direct Team Card */}
          <div className="bg-gradient-to-br from-green-500/10 to-transparent p-3 rounded-xl border border-green-500/20">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-green-400" size={18} />
              <span className="text-[10px] text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">Team</span>
            </div>
            <div className="text-lg sm:text-xl font-bold text-foreground">{dashboardData.activeDirects}</div>
            <div className="text-[10px] text-muted-foreground mt-1">Total: {dashboardData.totalDirects}</div>
          </div>

          {/* Total Rewards Card */}
          <div 
            onClick={() => handleCardClick('totalRewards')}
            className="bg-gradient-to-br from-purple-500/10 to-transparent p-3 rounded-xl border border-purple-500/20 cursor-pointer active:scale-95 transition"
          >
            <div className="flex items-center justify-between mb-2">
              <Trophy className="text-purple-400" size={18} />
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-purple-400 bg-purple-400/10 px-2 py-0.5 rounded-full">Rewards</span>
                {dashboardData.totalRewardsCount > 0 && (
                  <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full">
                    {dashboardData.totalRewardsCount}
                  </span>
                )}
              </div>
            </div>
            <div className="text-lg sm:text-xl font-bold text-foreground">{formatCurrency(dashboardData.totalRewards)}</div>
            <div className="text-[10px] text-muted-foreground mt-1 truncate">
              {dashboardData.teamSalesRewardsCount > 0 && (
                <span className="text-cyan-400 mr-1">
                  T:{dashboardData.teamSalesRewardsCount}
                </span>
              )}
              {dashboardData.salesRewardsCount > 0 && (
                <span className="text-green-400">
                  S:{dashboardData.salesRewardsCount}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation - Mobile Friendly Scrollable */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
              activeTab === 'overview' 
                ? 'bg-primary text-white' 
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            <Home size={16} className="inline mr-1" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('wallets')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
              activeTab === 'wallets' 
                ? 'bg-primary text-white' 
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            <Wallet size={16} className="inline mr-1" />
            Wallets
          </button>
          <button
            onClick={() => setActiveTab('income')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
              activeTab === 'income' 
                ? 'bg-primary text-white' 
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            <TrendingUp size={16} className="inline mr-1" />
            Income
          </button>
          <button
            onClick={() => setActiveTab('rewards')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
              activeTab === 'rewards' 
                ? 'bg-primary text-white' 
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            <Trophy size={16} className="inline mr-1" />
            Rewards
          </button>
        </div>

        {/* Overview Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Wallets Overview - Horizontal Scroll for Mobile */}
            <div className="bg-card rounded-xl p-4">
              <h2 className="text-base font-bold text-foreground mb-3 flex items-center gap-2">
                <Wallet className="text-primary" size={18} /> Your Wallets
              </h2>
              <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide">
                {/* Working Wallet */}
                <div 
                  onClick={() => handleCardClick('workingWallet')}
                  className="flex-shrink-0 w-36 bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <Activity className="text-primary mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">AV Power</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.workingWallet)}</div>
                </div>

                {/* Aerovolt Wallet */}
                <div 
                  onClick={() => handleCardClick('aurumWallet')}
                  className="flex-shrink-0 w-36 bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <Gem className="text-primary mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">Aerovolt</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.aurumWallet)}</div>
                </div>

                {/* Fund Wallet */}
                <div 
                  onClick={() => handleCardClick('fundWallet')}
                  className="flex-shrink-0 w-36 bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <CreditCard className="text-green-400 mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">AV PAY</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.fundWallet)}</div>
                </div>

                {/* Energy Wallet */}
                <div 
                  onClick={() => handleCardClick('energyWallet')}
                  className="flex-shrink-0 w-36 bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <Battery className="text-yellow-400 mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">AV Energy</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.energyWallet)}</div>
                </div>

                {/* ROI Wallet */}
                <div 
                  onClick={() => handleCardClick('roiWallet')}
                  className={`flex-shrink-0 w-36 bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition ${
                    dashboardData.hasBlockedROI ? 'border border-red-500/30' : ''
                  }`}
                >
                  <TrendingUp className={dashboardData.hasBlockedROI ? "text-red-400" : "text-orange-400"} size={20} />
                  <div className="text-xs text-foreground font-medium">AV ROI</div>
                  <div className={`text-base font-bold ${
                    dashboardData.hasBlockedROI ? 'text-red-400' : 'text-orange-400'
                  }`}>
                    {formatCurrency(dashboardData.roiWalletBalance)}
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Total Balance</span>
                  <span className="text-lg font-bold text-primary">{formatCurrency(dashboardData.totalWallets)}</span>
                </div>
              </div>
            </div>

            {/* Quick Income Stats */}
            <div className="bg-card rounded-xl p-4">
              <h2 className="text-base font-bold text-foreground mb-3 flex items-center gap-2">
                <TrendingUp className="text-primary" size={18} /> Income Snapshot
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary p-3 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">ROI Income</div>
                  <div className={`text-base font-bold ${dashboardData.hasBlockedROI ? 'text-red-400 line-through' : 'text-orange-400'}`}>
                    {formatCurrency(dashboardData.roiIncomeTotal)}
                  </div>
                </div>
                <div className="bg-secondary p-3 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Level Income</div>
                  <div className={`text-base font-bold ${dashboardData.hasBlockedLevelIncome ? 'text-red-400 line-through' : 'text-blue-400'}`}>
                    {formatCurrency(dashboardData.levelIncome)}
                  </div>
                  {dashboardData.levelIncomeCount > 0 && (
                    <div className="text-[10px] text-muted-foreground">{dashboardData.levelIncomeCount} txns</div>
                  )}
                </div>
                <div className="bg-secondary p-3 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Energy Income</div>
                  <div className="text-base font-bold text-yellow-400">{formatCurrency(dashboardData.energyIncome)}</div>
                </div>
                <div className="bg-secondary p-3 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Rewards</div>
                  <div className="text-base font-bold text-purple-400">{formatCurrency(dashboardData.totalRewards)}</div>
                </div>
              </div>
            </div>

            {/* Team Overview */}
            <div className="bg-card rounded-xl p-4">
              <h2 className="text-base font-bold text-foreground mb-3 flex items-center gap-2">
                <Users className="text-primary" size={18} /> Team Overview
              </h2>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-2xl font-bold text-foreground">{dashboardData.totalDirects}</div>
                  <div className="text-xs text-muted-foreground">Total Directs</div>
                </div>
                <div className="flex gap-3">
                  <div className="text-center">
                    <div className="text-sm font-bold text-green-400">{dashboardData.activeDirects}</div>
                    <div className="text-[10px] text-muted-foreground">Active</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-red-400">{dashboardData.inactiveDirects}</div>
                    <div className="text-[10px] text-muted-foreground">Inactive</div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleCardClick('team')}
                className="w-full py-2 bg-secondary rounded-lg text-sm text-foreground hover:bg-secondary/80 transition active:scale-95"
              >
                View Team
              </button>
            </div>
          </div>
        )}

        {/* Wallets Tab Content */}
        {activeTab === 'wallets' && (
          <div className="space-y-4">
            <div className="bg-card rounded-xl p-4">
              <h2 className="text-base font-bold text-foreground mb-3">All Wallets</h2>
              <div className="grid grid-cols-2 gap-3">
                {/* Working Wallet */}
                <div 
                  onClick={() => handleCardClick('workingWallet')}
                  className="bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <Activity className="text-primary mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">AV Power Wallet</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.workingWallet)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Level commissions</div>
                </div>

                {/* Aerovolt Wallet */}
                <div 
                  onClick={() => handleCardClick('aurumWallet')}
                  className="bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <Gem className="text-primary mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">Aerovolt Wallet</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.aurumWallet)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Main investment</div>
                </div>

                {/* Fund Wallet */}
                <div 
                  onClick={() => handleCardClick('fundWallet')}
                  className="bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <CreditCard className="text-green-400 mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">AV PAY Wallet</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.fundWallet)}</div>
                  <div className="text-[10px] text-green-400 mt-1">Deposits</div>
                </div>

                {/* Energy Wallet */}
                <div 
                  onClick={() => handleCardClick('energyWallet')}
                  className="bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition"
                >
                  <Battery className="text-yellow-400 mb-2" size={20} />
                  <div className="text-xs text-foreground font-medium">AV Energy Wallet</div>
                  <div className="text-base font-bold text-foreground">{formatCurrency(dashboardData.energyWallet)}</div>
                  <div className="text-[10px] text-yellow-400 mt-1">From referrals</div>
                </div>

                {/* ROI Wallet */}
                <div 
                  onClick={() => handleCardClick('roiWallet')}
                  className={`bg-secondary p-3 rounded-lg cursor-pointer active:scale-95 transition col-span-2 ${
                    dashboardData.hasBlockedROI ? 'border border-red-500/30' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <TrendingUp className={dashboardData.hasBlockedROI ? "text-red-400" : "text-orange-400"} size={20} />
                      <div className="text-xs text-foreground font-medium mt-1">AV ROI Wallet</div>
                      <div className={`text-lg font-bold ${
                        dashboardData.hasBlockedROI ? 'text-red-400' : 'text-orange-400'
                      }`}>
                        {formatCurrency(dashboardData.roiWalletBalance)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-muted-foreground">Daily Projection</div>
                      <div className={`text-sm font-bold ${dashboardData.hasBlockedROI ? 'text-red-400' : 'text-orange-400'}`}>
                        {formatCurrency(dashboardData.dailyRoiProjection)}
                      </div>
                      {dashboardData.blockedDailyROI > 0 && (
                        <div className="text-[10px] text-red-400">
                          <Shield size={10} className="inline mr-1" />
                          {formatCurrency(dashboardData.blockedDailyROI)} blocked
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Balance */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-foreground">Total Balance</span>
                  <span className="text-xl font-bold text-primary">{formatCurrency(dashboardData.totalWallets)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Income Tab Content */}
        {activeTab === 'income' && (
          <div className="space-y-4">
            <div className="bg-card rounded-xl p-4">
              <h2 className="text-base font-bold text-foreground mb-3">Income Breakdown</h2>
              
              {/* Total Income Card */}
              <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-4 rounded-lg mb-4">
                <div className="text-xs text-muted-foreground mb-1">Total Earnings</div>
                <div className="text-2xl font-bold text-primary">{formatCurrency(dashboardData.totalIncome)}</div>
              </div>

              {/* Income Sources */}
              <div className="space-y-3">
                {/* ROI Income */}
                <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={16} className={dashboardData.hasBlockedROI ? "text-red-400" : "text-orange-400"} />
                    <span className="text-sm text-foreground">ROI Income</span>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${dashboardData.hasBlockedROI ? 'text-red-400 line-through' : 'text-orange-400'}`}>
                      {formatCurrency(dashboardData.roiIncomeTotal)}
                    </div>
                    {dashboardData.hasBlockedROI && (
                      <div className="text-[10px] text-red-400">Blocked</div>
                    )}
                  </div>
                </div>

                {/* Level Income */}
                <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                  <div className="flex items-center gap-2">
                    <Layers size={16} className={dashboardData.hasBlockedLevelIncome ? "text-red-400" : "text-blue-400"} />
                    <span className="text-sm text-foreground">Level Income</span>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${dashboardData.hasBlockedLevelIncome ? 'text-red-400 line-through' : 'text-blue-400'}`}>
                      {formatCurrency(dashboardData.levelIncome)}
                    </div>
                    {dashboardData.levelIncomeCount > 0 && (
                      <div className="text-[10px] text-muted-foreground">{dashboardData.levelIncomeCount} transactions</div>
                    )}
                  </div>
                </div>

                {/* Energy Income */}
                <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                  <div className="flex items-center gap-2">
                    <Battery size={16} className="text-yellow-400" />
                    <span className="text-sm text-foreground">Energy Income</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-yellow-400">{formatCurrency(dashboardData.energyIncome)}</div>
                    {dashboardData.blockedEnergyIncome > 0 && (
                      <div className="text-[10px] text-red-400">{formatCurrency(dashboardData.blockedEnergyIncome)} blocked</div>
                    )}
                  </div>
                </div>

                {/* Rewards */}
                <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                  <div className="flex items-center gap-2">
                    <Trophy size={16} className="text-purple-400" />
                    <span className="text-sm text-foreground">Rewards</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-purple-400">{formatCurrency(dashboardData.totalRewards)}</div>
                    {dashboardData.totalRewardsCount > 0 && (
                      <div className="text-[10px] text-muted-foreground">{dashboardData.totalRewardsCount} rewards</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Blocked Income Summary */}
              {dashboardData.hasBlockedItems && (
                <div className="mt-4 p-3 bg-red-500/10 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={14} className="text-red-400" />
                    <span className="text-xs font-medium text-red-400">Blocked Income Summary</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {dashboardData.blockedROIIncome > 0 && (
                      <div className="text-red-400">ROI: {formatCurrency(dashboardData.blockedROIIncome)}</div>
                    )}
                    {dashboardData.levelIncomeBlockedAmount > 0 && (
                      <div className="text-red-400">Level: {formatCurrency(dashboardData.levelIncomeBlockedAmount)}</div>
                    )}
                    {dashboardData.blockedEnergyIncome > 0 && (
                      <div className="text-red-400">Energy: {formatCurrency(dashboardData.blockedEnergyIncome)}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rewards Tab Content */}
        {activeTab === 'rewards' && (
          <div className="space-y-4">
            <div className="bg-card rounded-xl p-4">
              <h2 className="text-base font-bold text-foreground mb-3">Your Rewards</h2>
              
              {/* Sales Rewards */}
              <div 
                onClick={() => handleCardClick('salesRewards')}
                className="bg-secondary p-4 rounded-lg mb-3 cursor-pointer active:scale-95 transition"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Target className="text-green-400" size={20} />
                    <span className="text-sm font-medium text-foreground">Sales Rewards</span>
                  </div>
                  {dashboardData.salesRewardsCount > 0 && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                      {dashboardData.salesRewardsCount}
                    </span>
                  )}
                </div>
                <div className="text-xl font-bold text-green-400 mb-1">{formatCurrency(dashboardData.salesRewards)}</div>
                <div className="text-xs text-muted-foreground">From sales challenges</div>
              </div>

              {/* Team Sales Rewards */}
              <div 
                onClick={() => handleCardClick('teamRewards')}
                className="bg-secondary p-4 rounded-lg cursor-pointer active:scale-95 transition"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="text-cyan-400" size={20} />
                    <span className="text-sm font-medium text-foreground">Team Sales Rewards</span>
                  </div>
                  {dashboardData.teamSalesRewardsCount > 0 && (
                    <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">
                      {dashboardData.teamSalesRewardsCount}
                    </span>
                  )}
                </div>
                <div className="text-xl font-bold text-cyan-400 mb-1">{formatCurrency(dashboardData.teamSalesRewards)}</div>
                <div className="text-xs text-muted-foreground">From team achievements</div>
              </div>

              {/* Total Rewards Summary */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-foreground">Total Rewards Earned</span>
                  <span className="text-lg font-bold text-purple-400">{formatCurrency(dashboardData.totalRewards)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 text-center">
                  {dashboardData.totalRewardsCount} reward{dashboardData.totalRewardsCount !== 1 ? 's' : ''} claimed
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hide Scrollbar Styles */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default UserDashboardHome;